/**
 * Deterministic math for the simulation and track generation.
 *
 * ECMAScript only guarantees bit-exact results for + - * /, Math.sqrt, and the
 * rounding helpers (floor, round, abs, min, max, imul...). Math.sin, cos, atan2,
 * exp, pow, hypot and the ** operator are "implementation-approximated" and can
 * differ between browser engines, which would break lap replay verification.
 *
 * Everything here is built from the exact operations only, so the same inputs
 * give the same bits in V8, SpiderMonkey and JavaScriptCore.
 */

export const PI = 3.141592653589793;
export const TWO_PI = 6.283185307179586;
export const HALF_PI = 1.5707963267948966;

// Cody-Waite split of pi/2 for a more accurate range reduction.
const HALF_PI_HI = 1.5707963267341256;
const HALF_PI_LO = 6.077100506506192e-11;
const INV_HALF_PI = 0.6366197723675814;

// Taylor coefficients, Horner form. |r| <= pi/4 after reduction, error < 1e-15.
function sinPoly(r: number): number {
  const r2 = r * r;
  return (
    r +
    r *
      r2 *
      (-1 / 6 +
        r2 *
          (1 / 120 +
            r2 * (-1 / 5040 + r2 * (1 / 362880 + r2 * (-1 / 39916800 + r2 * (1 / 6227020800))))))
  );
}

function cosPoly(r: number): number {
  const r2 = r * r;
  return (
    1 +
    r2 *
      (-1 / 2 +
        r2 *
          (1 / 24 +
            r2 * (-1 / 720 + r2 * (1 / 40320 + r2 * (-1 / 3628800 + r2 * (1 / 479001600))))))
  );
}

export function dsin(x: number): number {
  const q = Math.round(x * INV_HALF_PI);
  const r = x - q * HALF_PI_HI - q * HALF_PI_LO;
  switch (((q % 4) + 4) % 4) {
    case 0:
      return sinPoly(r);
    case 1:
      return cosPoly(r);
    case 2:
      return -sinPoly(r);
    default:
      return -cosPoly(r);
  }
}

export function dcos(x: number): number {
  const q = Math.round(x * INV_HALF_PI);
  const r = x - q * HALF_PI_HI - q * HALF_PI_LO;
  switch (((q % 4) + 4) % 4) {
    case 0:
      return cosPoly(r);
    case 1:
      return -sinPoly(r);
    case 2:
      return -cosPoly(r);
    default:
      return sinPoly(r);
  }
}

const TAN_PI_12 = 0.2679491924311227;
const SQRT3 = 1.7320508075688772;
const PI_6 = 0.5235987755982988;

/** atan for x in [0, 1]. */
function atanUnit(x: number): number {
  let offset = 0;
  if (x > TAN_PI_12) {
    // atan(x) = pi/6 + atan((x*sqrt3 - 1) / (sqrt3 + x))
    x = (x * SQRT3 - 1) / (SQRT3 + x);
    offset = PI_6;
  }
  // Taylor series, |x| <= tan(pi/12): 12 terms give < 1e-16.
  const x2 = x * x;
  let sum = 0;
  let term = x;
  for (let k = 0; k < 12; k++) {
    const c = term / (2 * k + 1);
    sum = k % 2 === 0 ? sum + c : sum - c;
    term *= x2;
  }
  return offset + sum;
}

export function datan(x: number): number {
  const neg = x < 0;
  let ax = neg ? -x : x;
  let r: number;
  if (ax > 1) {
    ax = 1 / ax;
    r = HALF_PI - atanUnit(ax);
  } else {
    r = atanUnit(ax);
  }
  return neg ? -r : r;
}

export function datan2(y: number, x: number): number {
  if (x === 0) {
    if (y > 0) return HALF_PI;
    if (y < 0) return -HALF_PI;
    return 0;
  }
  const a = datan(y / x);
  if (x > 0) return a;
  return y >= 0 ? a + PI : a - PI;
}

/** Wrap an angle to [-PI, PI). */
export function wrapAngle(a: number): number {
  return a - TWO_PI * Math.floor((a + PI) / TWO_PI);
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function sign(v: number): number {
  return v > 0 ? 1 : v < 0 ? -1 : 0;
}

/** Move `current` toward `target` by at most `maxDelta`. */
export function approach(current: number, target: number, maxDelta: number): number {
  if (current < target) return current + maxDelta > target ? target : current + maxDelta;
  return current - maxDelta < target ? target : current - maxDelta;
}

export function smoothstep01(t: number): number {
  const x = clamp(t, 0, 1);
  return x * x * (3 - 2 * x);
}

export function length2(x: number, y: number): number {
  return Math.sqrt(x * x + y * y);
}
